import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { Previous } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo, AppButton,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  initiateFetchAdminOnboardingDetails,
  clearAdminOnboardingDetails,
} from '@Actions';
import { NoDataPlaceholder } from '../../../Partial/Product/SharedComponents';

const Section = ({ title, children, page }) => (
  <Box
    direction="column"
    gap="0.75rem"
    pad="1.25rem"
    round="8px"
    border={{ color: page.tableBorder, size: '1px' }}
    background={page.tableBg}
  >
    <Text size="1rem" weight={600} color={page.primaryText}>
      {title}
    </Text>
    {children}
  </Box>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  page: PropTypes.shape({
    tableBorder: PropTypes.string,
    tableBg: PropTypes.string,
    primaryText: PropTypes.string,
  }).isRequired,
};

const Field = ({
  label, value = null, page, mono = false,
}) => (
  <Box direction="row" gap="0.5rem" align="start">
    <Text size="0.85rem" color={page.secondaryText} style={{ minWidth: '180px', flexShrink: 0 }}>
      {label}
    </Text>
    <Text
      size="0.9rem"
      color={page.tableText}
      style={mono ? { fontFamily: 'monospace', wordBreak: 'break-all' } : { wordBreak: 'break-word' }}
    >
      {value ?? '—'}
    </Text>
  </Box>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  page: PropTypes.shape({
    secondaryText: PropTypes.string,
    tableText: PropTypes.string,
  }).isRequired,
  mono: PropTypes.bool,
};

const StatusBadge = ({ value = null, colorMap, labelMap = null }) => {
  if (!value) return <Text size="0.9rem">—</Text>;
  const colors = colorMap[value] || { bg: '#71717a22', border: '#71717a', text: '#71717a' };
  const label = labelMap ? (labelMap[value] || value) : String(value).replace(/_/g, ' ');
  return (
    <Box
      background={colors.bg}
      border={{ color: colors.border, size: '1px' }}
      round="6px"
      pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
      width="fit-content"
    >
      <Text size="0.85rem" weight={600} color={colors.text}>
        {label}
      </Text>
    </Box>
  );
};

StatusBadge.propTypes = {
  value: PropTypes.string,
  colorMap: PropTypes.shape({}).isRequired,
  labelMap: PropTypes.shape({}),
};

const STATUS_COLORS = {
  prefilled: { bg: '#8b5cf622', border: '#8b5cf6', text: '#8b5cf6' },
  submitted: { bg: '#3b82f622', border: '#3b82f6', text: '#3b82f6' },
  /* eslint-disable camelcase */
  payment_pending: { bg: '#f59e0b22', border: '#f59e0b', text: '#f59e0b' },
  payment_success: { bg: '#00a85522', border: '#00a855', text: '#00a855' },
  /* eslint-enable camelcase */
};

const STATUS_LABELS = {
  prefilled: 'Onboarding Pending',
  submitted: 'Onboarding Completed',
  /* eslint-disable camelcase */
  payment_pending: 'Payment Pending',
  payment_success: 'Payment Completed',
  /* eslint-enable camelcase */
};

const INVOICE_STATUS_COLORS = {
  paid: { bg: '#00a85522', border: '#00a855', text: '#00a855' },
  /* eslint-disable camelcase */
  payment_due: { bg: '#e53e3e22', border: '#e53e3e', text: '#e53e3e' },
  /* eslint-enable camelcase */
  draft: { bg: '#71717a22', border: '#71717a', text: '#71717a' },
  sent: { bg: '#3b82f622', border: '#3b82f6', text: '#3b82f6' },
  void: { bg: '#71717a22', border: '#71717a', text: '#71717a' },
};

const INVOICE_STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  void: 'Void',
  /* eslint-disable camelcase */
  payment_due: 'Payment Due',
  /* eslint-enable camelcase */
};

const AdminOnboardingDetailsPage = ({
  small,
  location,
  loading,
  details = null,
  fetchDetails,
  clearDetails,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme: { page } } = useAppTheme();

  React.useEffect(() => {
    fetchDetails(id);
    return () => {
      clearDetails();
    };
  }, [id]);

  if (loading || !details) {
    return (
      <AdminNavWrapper
        small={small}
        location={location}
        bannerProps={{
          title: 'Onboarding Details',
          subTitle: 'Onboarding · Admin',
        }}
      >
        <Seo />
        <Box flex align="center" justify="center" pad="large">
          <NoDataPlaceholder
            loading={loading}
            label="Loading onboarding details..."
            noDataLabel="Onboarding not found."
            textColor={page.primaryText}
            loaderColor={page.highlight}
          />
        </Box>
      </AdminNavWrapper>
    );
  }

  const renderContent = () => {
    const billing = details.billing || {};
    const provisioning = details.provisioning || {};
    const contact = details.contact || {};
    const youtubeCid = details.youtubeCid || null;
    const socialScanning = details.socialScanning || null;

    return (
      <Box direction="column" gap="1rem" width="100%">
        {/* Header row */}
        <Box direction="row" align="center" justify="between" wrap>
          <Box direction="column" gap="0.25rem">
            <Text size="1.4rem" weight={600} color={page.primaryText}>
              {contact.companyName || contact.fullName || '—'}
            </Text>
            <Text size="0.9rem" color={page.secondaryText}>
              {contact.email}
              {contact.phone ? ` · ${contact.phone}` : ''}
              {contact.country ? ` · ${contact.country}` : ''}
            </Text>
          </Box>
          <Box direction="row" gap="0.5rem" align="center">
            <StatusBadge value={details.status} colorMap={STATUS_COLORS} labelMap={STATUS_LABELS} />
          </Box>
        </Box>

        {/* Contact & Account */}
        <Section title="Contact & Account" page={page}>
          <Field label="Full name" value={contact.fullName} page={page} />
          <Field label="Company name" value={contact.companyName} page={page} />
          <Field label="Email" value={contact.email} page={page} />
          <Field label="Phone" value={contact.phone} page={page} />
          <Field label="Country" value={contact.country} page={page} />
          <Field label="Account type" value={details.accountType} page={page} />
          <Field label="Watermarking" value={details.watermarking ? 'Yes' : 'No'} page={page} />
          <Field label="Referral token" value={details.referralToken} page={page} mono />
          <Field label="GHL contact ID" value={details.ghlContactId} page={page} mono />
          <Field label="Created" value={buildDateTimeLabel(details.createdAt)} page={page} />
          <Field label="Updated" value={buildDateTimeLabel(details.updatedAt)} page={page} />
        </Section>

        {/* Billing */}
        <Section title="Billing" page={page}>
          <Box direction="row" gap="0.5rem" align="center">
            <Text size="0.85rem" color={page.secondaryText} style={{ minWidth: '180px', flexShrink: 0 }}>
              Invoice status
            </Text>
            <StatusBadge
              value={billing.ghlInitialInvoiceStatus}
              colorMap={INVOICE_STATUS_COLORS}
              labelMap={INVOICE_STATUS_LABELS}
            />
          </Box>
          <Field label="Setup fee" value={billing.setupFeeAmount != null ? `$${billing.setupFeeAmount}` : null} page={page} />
          <Field label="Monthly fee" value={billing.monthlyFeeAmount != null ? `$${billing.monthlyFeeAmount}` : null} page={page} />
          <Field label="CID setup qty" value={billing.cidSetupQuantity} page={page} />
          <Field label="Social platforms" value={billing.socialPlatformCount} page={page} />
          <Field label="Invoice URL" value={billing.ghlInitialInvoiceUrl} page={page} mono />
          <Field label="Invoice ID" value={billing.ghlInitialInvoiceId} page={page} mono />
          <Field label="Recurring schedule ID" value={billing.ghlRecurringScheduleId} page={page} mono />
          <Field label="Activated at" value={billing.activatedAt ? buildDateTimeLabel(billing.activatedAt) : null} page={page} />
          <Field label="Pricing version" value={billing.pricingVersion} page={page} />
        </Section>

        {/* Provisioning */}
        <Section title="Provisioning" page={page}>
          <Field label="Company ID" value={provisioning.companyId} page={page} />
          <Field label="Primary user ID" value={provisioning.primaryUserId} page={page} />
          <Field label="Support user ID" value={provisioning.supportUserId} page={page} />
          <Field label="Payment pending" value={provisioning.paymentPending ? 'Yes' : 'No'} page={page} />
          <Field label="Payment link" value={provisioning.paymentLink} page={page} mono />
          <Field label="Content ID enabled" value={provisioning.features?.contentIdEnabled ? 'Yes' : 'No'} page={page} />
          <Field label="Search enabled" value={provisioning.features?.searchEnabled ? 'Yes' : 'No'} page={page} />
          <Field label="Last provisioned" value={provisioning.lastProvisionedAt ? buildDateTimeLabel(provisioning.lastProvisionedAt) : null} page={page} />
          {provisioning.warnings?.length > 0 && (
            <Box direction="column" gap="0.25rem">
              <Text size="0.85rem" color={page.secondaryText}>Warnings</Text>
              {provisioning.warnings.map((w) => (
                <Text key={w} size="0.85rem" color="#f59e0b">{w}</Text>
              ))}
            </Box>
          )}
        </Section>

        {/* YouTube CID */}
        {youtubeCid && (
          <Section title="YouTube Content ID" page={page}>
            <Field label="Uploader" value={youtubeCid.uploader} page={page} />
            <Field label="Plan" value={youtubeCid.plan} page={page} />
            {youtubeCid.channels?.length > 0 && (
              <Box direction="column" gap="0.25rem">
                <Text size="0.85rem" color={page.secondaryText}>Channels</Text>
                {youtubeCid.channels.map((ch) => (
                  <Text key={ch} size="0.85rem" color={page.tableText} style={{ fontFamily: 'monospace' }}>{ch}</Text>
                ))}
              </Box>
            )}
          </Section>
        )}

        {/* Social Scanning */}
        {socialScanning && (
          <Section title="Social Scanning" page={page}>
            <Field label="Main platform" value={socialScanning.mainPlatform} page={page} />
            <Field label="Main link" value={socialScanning.mainLink} page={page} mono />
            {socialScanning.platforms?.length > 0 && (
              <Box direction="column" gap="0.25rem">
                <Text size="0.85rem" color={page.secondaryText}>Platforms</Text>
                <Text size="0.85rem" color={page.tableText}>{socialScanning.platforms.join(', ')}</Text>
              </Box>
            )}
          </Section>
        )}

        {/* Agreement */}
        <Section title="Agreement" page={page}>
          <Field label="Accepted" value={details.agreement?.accepted ? 'Yes' : 'No'} page={page} />
          {details.signedAgreementUrl ? (
            <Box direction="row" gap="0.5rem" align="center">
              <Text size="0.85rem" color={page.secondaryText} style={{ minWidth: '180px', flexShrink: 0 }}>
                Signed agreement
              </Text>
              <AppButton
                plain
                level="tableControls"
                label="Download PDF"
                color={page.highlight}
                onClick={() => window.open(details.signedAgreementUrl, '_blank', 'noopener,noreferrer')}
              />
            </Box>
          ) : (
            <Field label="Signed agreement" value={null} page={page} />
          )}
        </Section>

        {/* Raw IDs */}
        <Section title="Identifiers" page={page}>
          {/* eslint-disable-next-line no-underscore-dangle */}
          <Field label="Onboarding ID" value={details.id || details._id} page={page} mono />
        </Section>
      </Box>
    );
  };

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Onboarding Details',
        subTitle: 'Onboarding · Admin',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={page.background}
        pad={{ vertical: '1.5rem', horizontal: small ? '1rem' : '2rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <Box direction="row" align="center" gap="0.5rem">
          <AppButton
            plain
            level="tableControls"
            label="Back to onboardings"
            color={page.highlight}
            icon={<Previous size="small" />}
            onClick={() => navigate(paths.adminOnboardings)}
          />
        </Box>
        {renderContent()}
      </Box>
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    details: state.adminOnboardings.details,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchDetails: initiateFetchAdminOnboardingDetails,
      clearDetails: clearAdminOnboardingDetails,
    },
    dispatch,
  );
}

AdminOnboardingDetailsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  details: PropTypes.shape({}),
  fetchDetails: PropTypes.func.isRequired,
  clearDetails: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminOnboardingDetailsPage);
